import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { CommonActions } from '@react-navigation/native';
import { Alert, Keyboard, ScrollView, View, StyleSheet } from 'react-native';
import Logo from 'design-system/Logo';
import Modal from 'utils/packages/Modal';
import Spacer from 'design-system/Spacer';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { muteErrorAndResultButThrows, useFetchOld } from 'hooks';
import { rem } from 'design-system';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { usePriceInputStyle } from '../hooks/usePriceInputStyle';
import { createStockAlert, editStockAlert } from '../actions';
import useStockMeta from '../hooks/useStockMeta';
import { useCurrentPrice } from '../hooks/selectors';
import { isWeb } from '../../../constants';
const SetStockAlertScreen = ({ route, navigation }) => {
    const [, , requesting, fetch] = useFetchOld(muteErrorAndResultButThrows);
    const { assetId } = route.params;
    const isEditFlow = 'id' in route.params;
    const colors = useColors();
    const textInputRef = useRef(null);
    const stockMeta = useStockMeta(assetId);
    const currentPrice = useCurrentPrice(assetId);
    const pendingStockAlerts = useAppSelector((state) => state.unpersistedInvestReducer.pendingStockAlerts?.[route.params.assetId]);
    const [price, setPrice] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const priceInputStyle = usePriceInputStyle(isFocused);
    useAutoFocusInput(textInputRef);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const onChangeText = useCallback((_formatted, extracted = '') => {
        setPrice(extracted);
    }, []);
    const focusInput = useCallback(() => {
        textInputRef.current?.focus();
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle, staticStyles.contentContainerStyles);
    const navigateAfterSave = useCallback(() => {
        navigation.navigateDeprecated('Success', {
            title: 'Success',
            subTitle: isEditFlow ? 'Alert has been successfully changed.' : `Alert has been successfully created.`,
            buttonText: 'Done',
            onContinue: () => {
                if (!isEditFlow) {
                    navigation.dispatch((state) => {
                        const routes = state.routes.filter((r) => !['InvestSearchStock'].includes(r.name));
                        return CommonActions.reset({
                            ...state,
                            routes,
                            index: routes.length - 1,
                        });
                    });
                }
                navigation.pop(2);
                return true;
            },
        });
    }, [isEditFlow]);
    const handleCreateAlert = useCallback(async () => {
        const parsedPrice = Number(price.replace(/[^0-9.]/g, ''));
        if (!parsedPrice) {
            Alert.alert('Hold on 💫', 'Please enter a valid amount.');
            return;
        }
        Keyboard.dismiss();
        if (isEditFlow) {
            if (route.params.alertPriceValue === parsedPrice) {
                navigation.pop();
                return;
            }
        }
        const hasAlert = pendingStockAlerts?.find((alert) => alert.targetPrice.value === parsedPrice);
        if (hasAlert) {
            Modal.showError('You already have an alert at this price. Please choose another price.', focusInput, 'Alert already exists', 'OK, thanks');
            return;
        }
        try {
            if (isEditFlow) {
                await fetch(editStockAlert(route.params.id, route.params.assetId, {
                    value: parsedPrice,
                    currency: 'USD',
                }));
                navigateAfterSave();
            }
            else if (currentPrice) {
                // should have current price here
                await fetch(createStockAlert(route.params.assetId, {
                    value: parsedPrice,
                    currency: 'USD',
                }, {
                    value: currentPrice.value,
                    currency: currentPrice.currency,
                }));
                navigateAfterSave();
            }
        }
        catch {
            //
        }
    }, [currentPrice, focusInput, isEditFlow, pendingStockAlerts, price, route.params, navigateAfterSave]);
    return (<AppView withFrame={false} avoidHeader>
      <ScrollView scrollEnabled={false} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle}>
        <View style={staticStyles.flex}>
          <Spacer h={16}/>
          <ScreenInfoHeader smallSubTitle title={stockMeta?.name ? `Set ${stockMeta.name} alert` : 'Set price alert'} subTitle={stockMeta?.symbol ? `Alert triggers when 1 $${stockMeta.symbol} equals:` : ' '}/>
          <CardView withPadding>
            <View style={staticStyles.row}>
              <View style={staticStyles.flex}>
                <MoneyTextInput value={price} testID="input" ref={textInputRef} style={priceInputStyle} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} allowFontScaling={false} prefix="$" placeholder="$0.00" onChangeText={onChangeText} onSubmitEditing={handleCreateAlert} underlineColorAndroid="transparent" selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder}/>
                {!!currentPrice && (<Text TextThin-14 Secondary>
                    Current price: {formatAmount(currentPrice.value, currentPrice.currency, 2)}
                  </Text>)}
              </View>
              <Spacer w={16}/>
              <Logo R44 uri={stockMeta?.iconUrl} resizeMode="contain"/>
            </View>
          </CardView>
          <View style={staticStyles.textContainer}>
            <Text TextThin-14 Secondary>
              You will receive an alert when the stock price reaches your chosen amount
            </Text>
          </View>
        </View>
      </ScrollView>
      <KeyboardAvoidingButton brand isFetching={requesting} onPress={handleCreateAlert} title={isEditFlow ? 'Save' : 'Create alert'}/>
    </AppView>);
};
const staticStyles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    textContainer: {
        paddingHorizontal: rem(16),
    },
    contentContainerStyles: {
        flexGrow: 1,
    },
});
export default SetStockAlertScreen;
